<script setup>
/**
 * Anchor mark for the control-centre Anchor column (PD-1258).
 *
 * Anchors are rare, so the mark stays quiet: a small pill carrying the anchor
 * glyph, promoted to a solid accent "CEO" pill for the one role holding the
 * framework ceiling. Renders an em-dash when the role isn't an anchor, so the
 * column reads as a column rather than as scattered marks.
 */
import { computed } from 'vue';
import { LEVELLING_ANCHOR } from '@rm-ui/levelling/levellingMeta';

const props = defineProps({
  isAnchor: { type: Boolean, default: false },
  isCeiling: { type: Boolean, default: false },
  /** Picked by the setup wizard but not yet evaluated — a claim, not evidence. */
  isPending: { type: Boolean, default: false },
  rationale: { type: String, default: null },
});

/* The glyph is ALWAYS the anchor, per the design prototype's lvlAnchorChip:
     <MS name="anchor" size={13} />{info.ceo ? <span className="k">CEO</span> : null}
   State is carried by the PILL, not the icon — filled accent + a "CEO" label for the
   ceiling, dashed grey for pending. Both classes can apply at once (a pending
   ceiling), and --pending is defined last so it wins the shared properties. */

const tip = computed(() => {
  if (!props.isAnchor) return '';
  // Pending first: it qualifies everything else, including the ceiling.
  if (props.isPending) return LEVELLING_ANCHOR.pending.tip;
  if (props.isCeiling) return LEVELLING_ANCHOR.ceiling.tip;
  return props.rationale
    ? `Anchor role: ${props.rationale}`
    : `${LEVELLING_ANCHOR.anchor.tip} No rationale recorded yet.`;
});
</script>

<template>
  <span
    v-if="isAnchor"
    class="lvl-anchor-chip"
    :class="{ 'lvl-anchor-chip--ceo': isCeiling, 'lvl-anchor-chip--pending': isPending }"
    :title="tip"
  >
    <v-icon size="13">{{ LEVELLING_ANCHOR.anchor.mdi }}</v-icon>
    <span v-if="isCeiling" class="k">{{ LEVELLING_ANCHOR.ceiling.short }}</span>
  </span>
  <span v-else class="lvl-anchor-cell-none">—</span>
</template>

<style lang="scss" scoped>
/* Ported from the design prototype's lvl.css anchor block, against the --cc-*
   tokens theme.scss declares globally for light and dark. */
.lvl-anchor-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: none;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--cc-accent, #1d5bd6) 35%, transparent);
  background: color-mix(in srgb, var(--cc-accent, #1d5bd6) 9%, transparent);
  color: var(--cc-accent, #1d5bd6);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;

  .k {
    line-height: 1;
  }

  ::v-deep(.v-icon) {
    color: currentColor !important;
    font-size: 13px !important;
  }

  &.lvl-anchor-chip--ceo {
    background: var(--cc-accent, #1d5bd6);
    border-color: var(--cc-accent, #1d5bd6);
    color: #fff;
  }

  /* Dashed and neutral: a claim, not evidence. Paired with the pending glyph so
     it never reads as an established anchor. */
  &.lvl-anchor-chip--pending {
    border-style: dashed;
    background: transparent;
    color: var(--cc-text-3, #97a0af);
    border-color: var(--cc-border, #e5e7eb);
  }
}

.lvl-anchor-cell-none {
  color: var(--cc-text-3, #97a0af);
  opacity: 0.55;
  font-size: 12px;
}
</style>
